import React, { useState, useRef, useEffect } from 'react';
import { getRecentErrors } from '../utils/errorCollector';

const FEEDBACK_API = 'https://feedback.badideaco.com/api/feedback';

// Capture a pixel-perfect screenshot via the Screen Capture API.
async function captureScreen() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    preferCurrentTab: true,
  });
  const track = stream.getVideoTracks()[0];
  const video = document.createElement('video');
  video.srcObject = stream;
  await video.play();
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0);
  track.stop();
  return canvas.toDataURL('image/png');
}

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [notes, setNotes] = useState('');
  const [submitterName, setSubmitterName] = useState(() => localStorage.getItem('feedback_name') || '');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);
  const [consoleErrors, setConsoleErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [expandScreenshot, setExpandScreenshot] = useState(false);
  const notesRef = useRef();

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleOpen = async () => {
    try {
      const dataUrl = await captureScreen();
      setScreenshotDataUrl(dataUrl);
    } catch (e) {
      console.error('Screenshot capture failed:', e);
      setScreenshotDataUrl(null);
    }
    setConsoleErrors(getRecentErrors());
    setType('bug');
    setNotes('');
    setShowErrors(false);
    setExpandScreenshot(false);
    setOpen(true);
    setTimeout(() => notesRef.current?.focus(), 100);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    setScreenshotDataUrl(null);
  };

  const detectCurrentView = () => {
    // JobQ navbar — find the active nav link
    const active = document.querySelector('.navbar .nav-link.active');
    if (active) return active.textContent.trim();
    // Fallback: check page heading
    const heading = document.querySelector('h2, h3, .page-title');
    return heading?.textContent?.trim()?.substring(0, 50) || 'unknown';
  };

  const handleSubmit = async () => {
    if (type === 'feature' && !notes.trim()) {
      setToast({ type: 'error', message: 'Please describe the feature you\'d like.' });
      notesRef.current?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const screenshotB64 = screenshotDataUrl
        ? screenshotDataUrl.replace(/^data:image\/png;base64,/, '')
        : null;

      if (submitterName.trim()) localStorage.setItem('feedback_name', submitterName.trim());

      const res = await fetch(FEEDBACK_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          notes: notes.trim(),
          submitted_by: submitterName.trim() || null,
          screenshot_b64: screenshotB64,
          console_errors: type === 'bug' ? consoleErrors : [],
          current_view: detectCurrentView(),
          url: window.location.href,
          user_agent: navigator.userAgent,
          source_app: 'jobq',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setToast({ type: 'success', message: 'Feedback submitted! Thank you.' });
      setOpen(false);
      setScreenshotDataUrl(null);
    } catch (e) {
      console.error('Feedback submit failed:', e);
      setToast({ type: 'error', message: 'Failed to submit feedback. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={handleOpen}
        title="Report a bug or request a feature"
        style={styles.fab}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0056b3'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#007bff'; }}
      >
        <i className="fas fa-bullhorn" style={{ fontSize: 20 }} />
      </button>

      {/* Modal Backdrop */}
      {open && (
        <div style={styles.backdrop} onClick={handleClose}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={styles.header}>
              <h5 style={{ margin: 0, fontWeight: 600 }}>Send Feedback</h5>
              <button onClick={handleClose} style={styles.closeBtn}>&times;</button>
            </div>

            {/* Body */}
            <div style={styles.body}>
              {/* Type Toggle */}
              <div className="btn-group w-100 mb-3" role="group">
                <button
                  className={`btn ${type === 'bug' ? 'btn-danger' : 'btn-outline-secondary'}`}
                  onClick={() => setType('bug')}
                  style={{ flex: 1 }}
                >
                  <i className="fas fa-bug me-1" /> Bug Report
                </button>
                <button
                  className={`btn ${type === 'feature' ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => setType('feature')}
                  style={{ flex: 1 }}
                >
                  <i className="fas fa-magic me-1" /> Feature Request
                </button>
              </div>

              {/* Your Name */}
              <div className="mb-3">
                <label className="form-label small text-muted">Your Name (optional)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={submitterName}
                  onChange={(e) => setSubmitterName(e.target.value)}
                  placeholder="So we know who to follow up with"
                />
              </div>

              {/* Screenshot Preview */}
              {screenshotDataUrl ? (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Screenshot (auto-captured)</small>
                  <div
                    onClick={() => setExpandScreenshot(!expandScreenshot)}
                    style={styles.screenshotContainer}
                  >
                    <img
                      src={screenshotDataUrl}
                      alt="Screenshot preview"
                      style={{
                        width: '100%',
                        maxHeight: expandScreenshot ? 'none' : 160,
                        objectFit: 'cover',
                        objectPosition: 'top',
                        display: 'block',
                      }}
                    />
                  </div>
                  <small className="text-muted">Click to {expandScreenshot ? 'collapse' : 'expand'}</small>
                </div>
              ) : (
                <small className="text-muted fst-italic d-block mb-3">Screenshot capture unavailable</small>
              )}

              {/* Notes */}
              <div className="mb-3">
                <label className="form-label small text-muted">
                  {type === 'bug' ? 'What went wrong?' : 'Describe the feature you\'d like'}
                  {type === 'feature' && <span className="text-danger ms-1">*</span>}
                </label>
                <textarea
                  ref={notesRef}
                  className="form-control form-control-sm"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={type === 'bug'
                    ? 'Describe the issue (optional \u2014 the screenshot may be enough)...'
                    : 'Describe the feature or improvement you\'d like to see...'}
                />
              </div>

              {/* Console Errors (bug reports only) */}
              {type === 'bug' && consoleErrors.length > 0 && (
                <div className="mb-3">
                  <button
                    className="btn btn-sm btn-link text-muted p-0"
                    onClick={() => setShowErrors(!showErrors)}
                    style={{ textDecoration: 'none', fontSize: '0.75rem' }}
                  >
                    <i className={`fas fa-chevron-${showErrors ? 'down' : 'right'} me-1`} />
                    {consoleErrors.length} console error{consoleErrors.length !== 1 ? 's' : ''} captured
                  </button>
                  {showErrors && (
                    <div style={styles.errorBox}>
                      {consoleErrors.map((err, i) => (
                        <div key={i} style={styles.errorLine}>
                          <span style={{ color: '#858585' }}>{new Date(err.timestamp).toLocaleTimeString()}</span>{' '}
                          {err.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {type === 'bug' && consoleErrors.length === 0 && (
                <small className="text-muted d-block mb-3">No recent console errors captured.</small>
              )}
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <button className="btn btn-sm btn-outline-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                className={`btn btn-sm ${type === 'bug' ? 'btn-danger' : 'btn-warning'}`}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.type === 'success' ? '#d4edda' : '#f8d7da',
          color: toast.type === 'success' ? '#155724' : '#721c24',
          borderColor: toast.type === 'success' ? '#c3e6cb' : '#f5c6cb',
        }}>
          {toast.message}
        </div>
      )}
    </>
  );
}

const styles = {
  fab: {
    position: 'fixed',
    bottom: 88,
    left: 24,
    zIndex: 99990,
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s, box-shadow 0.2s',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 99991,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 12,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: 500,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: 24,
    color: '#6c757d',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
  },
  body: {
    padding: '16px 20px',
    overflowY: 'auto',
    flex: 1,
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  screenshotContainer: {
    border: '1px solid #dee2e6',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  errorBox: {
    backgroundColor: '#1e1e1e',
    borderRadius: 8,
    padding: 12,
    maxHeight: 150,
    overflowY: 'auto',
    marginTop: 8,
  },
  errorLine: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: '#f48771',
    marginBottom: 4,
  },
  toast: {
    position: 'fixed',
    top: 16,
    left: 16,
    zIndex: 99993,
    padding: '12px 16px',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    fontSize: '0.875rem',
    fontWeight: 500,
    border: '1px solid',
  },
};
